// Moved from `src/hooks/mutation/useMutateDeleteOrganizationTeamMemberById.ts`
// (now the app delegate) with the OT4 mutation slice (ARCFE-1033).
//
// TOASTS: PORTS -> META (ARCFE-1082, meta v1.1). `onDeleted` and
// `onDeleteError` are GONE. Both toasts were this hook's only app coupling, so
// the copy now travels with the mutation as `meta` — inert data typed by
// `@heidi/api/meta`, rendered by the single `MutationCache` subscriber in
// `src/utils/reactQueryClient.ts` — and the delegate dissolved to a pure
// `export { … } from '@heidi/workspace/data'` shim. The strings are
// transcribed byte-identically and were hardcoded English at every prior site.
//
// THE SUCCESS ORDERING IS UNCHANGED, AND THAT MATTERED HERE. `onSuccess`
// RETURNS the invalidation promise, so TanStack awaits it; the toast used to
// fire on the line above that return. The cache-level handler fires BEFORE
// `options.onSuccess` runs at all, so the toast still precedes the
// invalidation. Same relative order, one step earlier.
//
// REHYDRATION delta, disclosed: `meta` is plain JSON and survives dehydration
// where a component-bound callback did not. Theoretical for workspace:
// `src/utils/reactQueryPersistConfig.ts` sets only `shouldDehydrateQuery`,
// leaving `shouldDehydrateMutation` at the TanStack default of `isPaused`, so
// only a mutation fired OFFLINE and still paused at unload can be persisted.
import { logger } from '@heidi/runtime/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_ORGANIZATION_MEMBERS } from '../../contracts';
import { deleteOrganizationTeamMember } from '../api/organization';
import { ORGANIZATION_MEMBER_TOASTS } from './organizationMemberToasts.messages';

export interface DeleteOrganizationTeamMemberByIdVariables {
  organizationId: string;
  teamId: string;
  memberId: string;
}

/** DELETE /organization/{organization_id}/teams/{team_id}/members/{member_id} */
export function useMutateDeleteOrganizationTeamMemberById() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['DeleteOrganizationTeamMemberById'],
    mutationFn: async (input: DeleteOrganizationTeamMemberByIdVariables) => {
      return await deleteOrganizationTeamMember(
        input.organizationId,
        input.teamId,
        input.memberId
      );
    },
    onSuccess: (_, input) => {
      return queryClient.invalidateQueries({
        queryKey: [GET_ORGANIZATION_MEMBERS, input.organizationId],
      });
    },
    onError: (error) => {
      logger.error('Error deleting team member:', error);
    },
    meta: ORGANIZATION_MEMBER_TOASTS.deleteTeamMember,
  });
}
